import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useState } from 'react'
import { usePageTitle } from '../hooks/usePageTitle'
import { Link } from '../i18n/LocalizedLink'
import EditorialThumbnail, { EditorialGridSkeleton } from '../components/EditorialThumbnail'
import Navbar from '../components/Navbar'
import CTAStrip from '../components/CTAStrip'
import Footer from '../components/Footer'
import TickerBand from '../components/TickerBand'
import { listPublishedPosts } from '../services/blogService'
import { usePreloadedData } from '../PreloadedDataContext'
import { DEFAULT_THUMBNAIL, type Publication, type BlogPost } from '../types/blog'
import './NewsroomPage.css'
import './BlogPage.css'

function formatDate(value: string | null, locale: 'en' | 'ja') {
  if (!value) return ''
  return new Intl.DateTimeFormat(locale === 'ja' ? 'ja-JP' : 'en-US', {
    year: 'numeric',
    month: locale === 'ja' ? 'long' : 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

function BlogCard({ post, featured = false }: { post: BlogPost; featured?: boolean }) {
  const { locale, t } = useLocale()
  return (
    <article className={`nr-card ${featured ? 'nr-card--featured' : ''}`}>
      <Link to={`/${post.publication}/${post.slug}`} className="blog-card-cover-link" aria-label={post.title}>
        <EditorialThumbnail
          src={post.coverImage || DEFAULT_THUMBNAIL}
          featured={featured}
        />
      </Link>
      <div className="nr-card-body">
        <span className="nr-card-category">{post.category.toUpperCase()}</span>
        <h3 className="nr-card-title"><Link to={`/${post.publication}/${post.slug}`}>{post.title}</Link></h3>
        {featured && <p className="nr-card-excerpt">{post.excerpt}</p>}
        {post.coverImageCredit && (
          <p className="blog-card-credit"> {t("Photo by")}{' '}
            <a href={post.coverImageCredit.authorUrl} target="_blank" rel="noopener noreferrer">{post.coverImageCredit.author}</a>
            {' '}{t("on")}{' '}
            <a href={post.coverImageCredit.sourceUrl} target="_blank" rel="noopener noreferrer">Unsplash</a>
          </p>
        )}
        <span className="nr-card-date">{formatDate(post.publishedAt, locale)}</span>
      </div>
      <Link to={`/${post.publication}/${post.slug}`} className="nr-card-arrow" aria-label={t('Read {title}', { title: post.title })}>→</Link>
    </article>
  )
}

export default function BlogPage({ publication = 'blog' }: { publication?: Publication }) {
  const { t } = useLocale()
  const newsroom = publication === 'newsroom'
  usePageTitle(newsroom ? t("Newsroom") : t("Blog"))
  const preloadedData = usePreloadedData()
  const preloadedPosts = preloadedData?.kind === `${publication}-index`
    ? preloadedData.posts
    : null
  const [posts, setPosts] = useState<BlogPost[]>(() => preloadedPosts ?? [])
  const [loading, setLoading] = useState(preloadedPosts === null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    listPublishedPosts(publication)
      .then(items => {
        if (!active) return
        setPosts(items)
        setFailed(false)
      })
      .catch(() => {
        if (active && preloadedPosts === null) setFailed(true)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [preloadedPosts, publication])

  const [featured, ...rest] = posts

  return (
    <>
      <Navbar heroSelector=".nr-header" />

      <div className="nr-page blog-page">
        <div className="nr-header">
          <div className="nr-header-inner container">
            <h1 className="nr-header-title" lang="en">{newsroom ? "Newsroom" : "Blog"}</h1>
            <p className={`nr-header-sub${newsroom ? ' jp' : ''}`}>
              {t(newsroom ? 'Company updates and press releases from Bybeit, Inc.' : 'Ideas and practical perspectives on building, launching, and growing in Japan.')}
            </p>
          </div>
        </div>

        <TickerBand />

        <main className="nr-main container">
          <h2 className="nr-section-label">{newsroom ? t("Latest News") : t("Latest Stories")}</h2>
          {loading ? (
            <EditorialGridSkeleton />
          ) : posts.length > 0 ? (
            <div className="nr-grid">
              <BlogCard post={featured} featured />
              {rest.map(post => <BlogCard key={post.slug} post={post} />)}
            </div>
          ) : (
            <div className="blog-empty" role="status">
              <p className="blog-empty-title">
                {failed ? t("Stories are temporarily unavailable.") : t("Stories are on the way.")}
              </p>
              <p className="blog-empty-copy">
                {failed
                  ? t("Please check back shortly.")
                  : t("We’re preparing field notes, market perspectives, and lessons from the work.")}
              </p>
            </div>
          )}
        </main>
      </div>

      <CTAStrip />
      <Footer />
    </>
  )
}
